// Collection page functionality
// Handles collection filtering, sorting, pagination, and product grid updates

// --- Selectors ---
const collectionSelectors = {
  collectionSection: '[data-section-type="collection-template"]',
  productGridContainer: "#product-grid-container",
  sortBySelect: ".ajax-sort-select",
  filterCheckboxes: ".ajax-filter-checkbox",
  priceInputs: ".ajax-price-input",
  mobileFilterTrigger: "#mobile-filter-trigger",
  mobileFilters: "#mobile-filters",
  mobileFilterClose: "#mobile-filter-close",
  mobileFilterOverlay: "#mobile-filter-overlay"
};

// --- Collection Functions ---

/**
 * Replaces the entire product grid and pagination with new content
 * @param {string} url - URL to fetch new content from
 */
function updateCollectionContent(url) {
  console.log('Collection: Updating collection content with URL:', url);

  const collectionSection = document.querySelector(collectionSelectors.collectionSection);
  if (collectionSection) {
    showLoading(collectionSection);
  }

  fetch(url)
    .then((response) => {
      console.log('Collection: Fetch response received:', response.status);
      return response.text();
    })
    .then((responseText) => {
      console.log('Collection: Processing response text');

      // Create a temporary container to parse the fetched HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = responseText;

      // Extract the new product grid and pagination
      const newGrid = tempDiv.querySelector("#product-grid-container")?.innerHTML;
      const newPagination = tempDiv.querySelector(".mt-16 .pagination");
      const newFilters = tempDiv.querySelector("#collection-filters-sidebar")?.innerHTML;

      console.log('Collection: Extracted content - Grid:', !!newGrid, 'Pagination:', !!newPagination, 'Filters:', !!newFilters);

      // Update the DOM elements
      const productGridContainer = document.querySelector(collectionSelectors.productGridContainer);
      if (productGridContainer && newGrid) {
        productGridContainer.innerHTML = newGrid;
        console.log('Collection: Product grid updated');
      }

      const currentPagination = document.querySelector(".mt-16 .pagination");
      if (currentPagination && newPagination) {
        currentPagination.replaceWith(newPagination);
        console.log('Collection: Pagination updated');
      }

      const currentFilters = document.getElementById("collection-filters-sidebar");
      if (currentFilters && newFilters) {
        currentFilters.innerHTML = newFilters;
        console.log('Collection: Filters sidebar updated');
      }

      // Hide loading state and re-initialize listeners
      if (collectionSection) {
        hideLoading(collectionSection);
      }
      initCollectionListeners();

      console.log('Collection: Content update complete');
    })
    .catch((error) => {
      console.error("Collection: Error fetching collection content:", error);
      if (collectionSection) {
        hideLoading(collectionSection);
      }
    });
}

/**
 * Builds the new URL based on current sort/filter selections
 */
function getNewCollectionUrl() {
  console.log('Collection: Building new collection URL');

  // Get the base URL (collection URL without params)
  const baseUrl = window.location.pathname.split("?")[0];
  const params = new URLSearchParams(window.location.search);

  console.log('Collection: Base URL:', baseUrl);
  console.log('Collection: Current params:', params.toString());

  // Get Sort By value
  const sortBySelect = document.querySelector(collectionSelectors.sortBySelect);
  if (sortBySelect) {
    params.set("sort_by", sortBySelect.value);
    console.log('Collection: Sort by:', sortBySelect.value);
  }

  // Get price range values from both desktop and mobile inputs
  const priceMin = (
    document.getElementById("price-min") ||
    document.getElementById("mobile-price-min")
  )?.value;
  const priceMax = (
    document.getElementById("price-max") ||
    document.getElementById("mobile-price-max")
  )?.value;

  if (priceMin) {
    params.set("filter.v.price.gte", priceMin);
    console.log('Collection: Min price:', priceMin);
  }
  if (priceMax) {
    params.set("filter.v.price.lte", priceMax);
    console.log('Collection: Max price:', priceMax);
  }

  const queryString = params.toString();
  const newUrl = `${baseUrl}?${queryString}`;

  console.log('Collection: New URL:', newUrl);
  return newUrl;
}

/**
 * Handles sorting changes
 */
function handleSortChange() {
  console.log('Collection: Sort change detected');
  const url = getNewCollectionUrl();
  updateCollectionContent(url);
  history.pushState({}, "", url);
}

/**
 * Handles filter checkbox changes
 * @param {Event} event - Change event
 */
function handleFilterChange(event) {
  console.log('Collection: Filter change detected:', event.target);

  event.preventDefault();
  let newUrl;

  // Check if the filter is being added or removed
  if (event.target.checked) {
    console.log('Collection: Adding filter:', event.target.dataset.urlToAdd);
    newUrl = event.target.dataset.urlToAdd;
  } else {
    console.log('Collection: Removing filter:', event.target.dataset.urlToRemove);
    newUrl = event.target.dataset.urlToRemove;
  }

  updateCollectionContent(newUrl);
  history.pushState({}, "", newUrl);
}

/**
 * Handles price range input changes
 */
function handlePriceChange() {
  console.log('Collection: Price range changed');
  const url = getNewCollectionUrl();
  updateCollectionContent(url);
  history.pushState({}, "", url);
}

/**
 * Handles pagination link clicks
 * @param {Event} event - Click event
 */
function handlePaginationClick(event) {
  const link = event.target.closest(".pagination a");
  if (link) {
    console.log('Collection: Pagination clicked:', link.href);
    event.preventDefault();
    const url = link.href;
    updateCollectionContent(url);
    history.pushState({}, "", url);
  }
}

/**
 * Handles mobile filter drawer toggle
 */
function handleMobileFilterToggle() {
  console.log('Collection: Mobile filter toggle clicked');
  const drawer = document.querySelector(collectionSelectors.mobileFilters);
  if (drawer) {
    drawer.classList.remove('hidden');
  }
}

/**
 * Handles mobile filter drawer close
 */
function handleMobileFilterClose() {
  console.log('Collection: Mobile filter close clicked');
  const drawer = document.querySelector(collectionSelectors.mobileFilters);
  if (drawer) {
    drawer.classList.add('hidden');
  }
}

/**
 * Handles mobile filter overlay click
 */
function handleMobileFilterOverlay() {
  console.log('Collection: Mobile filter overlay clicked');
  const drawer = document.querySelector(collectionSelectors.mobileFilters);
  if (drawer) {
    drawer.classList.add('hidden');
  }
}

/**
 * Initializes all event listeners for the collection page
 */
function initCollectionListeners() {
  console.log('Collection: Initializing collection listeners');

  // Remove existing listeners first to prevent duplicates
  const sortBySelect = document.querySelector(collectionSelectors.sortBySelect);
  if (sortBySelect) {
    sortBySelect.removeEventListener("change", handleSortChange);
    sortBySelect.addEventListener("change", handleSortChange);
  }

  // Filter checkboxes
  document.querySelectorAll(collectionSelectors.filterCheckboxes).forEach((checkbox) => {
    checkbox.removeEventListener("change", handleFilterChange);
    checkbox.addEventListener("change", handleFilterChange);
  });

  // Price range inputs (with debounce)
  document.querySelectorAll(collectionSelectors.priceInputs).forEach((input) => {
    input.removeEventListener("input", debounce(handlePriceChange, 500));
    input.addEventListener("input", debounce(handlePriceChange, 500));
  });

  // Pagination links
  document.removeEventListener("click", handlePaginationClick);
  document.addEventListener("click", handlePaginationClick);

  // Mobile filter controls
  const mobileFilterTrigger = document.querySelector(collectionSelectors.mobileFilterTrigger);
  if (mobileFilterTrigger) {
    mobileFilterTrigger.removeEventListener("click", handleMobileFilterToggle);
    mobileFilterTrigger.addEventListener("click", handleMobileFilterToggle);
  }

  const mobileFilterClose = document.querySelector(collectionSelectors.mobileFilterClose);
  if (mobileFilterClose) {
    mobileFilterClose.removeEventListener("click", handleMobileFilterClose);
    mobileFilterClose.addEventListener("click", handleMobileFilterClose);
  }

  const mobileFilterOverlay = document.querySelector(collectionSelectors.mobileFilterOverlay);
  if (mobileFilterOverlay) {
    mobileFilterOverlay.removeEventListener("click", handleMobileFilterOverlay);
    mobileFilterOverlay.addEventListener("click", handleMobileFilterOverlay);
  }

  console.log('Collection: Collection listeners initialized');
}

// --- Initialization ---

function initCollection() {
  console.log('Collection: Initializing collection functionality');

  const collectionSection = document.querySelector(collectionSelectors.collectionSection);
  const productGridContainer = document.querySelector(collectionSelectors.productGridContainer);

  if (collectionSection && productGridContainer) {
    console.log('Collection: Collection section and grid found, initializing');
    initCollectionListeners();
  } else {
    console.log('Collection: Collection section or grid not found, skipping initialization');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollection);
} else {
  initCollection();
}

// Export functions for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    updateCollectionContent,
    getNewCollectionUrl,
    handleSortChange,
    handleFilterChange,
    handlePriceChange,
    handlePaginationClick,
    initCollectionListeners,
    initCollection
  };
}